/**
 * frontend/src/pages/valuation/ValuationFinancials.tsx
 *
 * VAL-UPLOAD-U1B-1 — the "Financials" step, between Company and Intake. The
 * valuator uploads statements, presses "Read figures", and sees one column per
 * fiscal year with where each figure came from. Held years and held units are
 * answered here (Confirm / Discard, Confirm / Decline). Read-only: nothing is
 * typed into the valuation and no figure is computed in the browser (U2 edits).
 *
 * Data provenance: every decision is a server call; the screen shows what the
 * server holds after each one. No browser storage.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { toast } from 'sonner'
import {
  extractFinancials,
  decideTieout,
  fetchFinancials,
  saveFinancials,
  uploadFinancialsDocument,
  type ExtractBody,
  type FinancialsView,
} from '@/api/valuationFinancials'
import { formatApiError } from '@/utils/httpErrors'
import { StepBlocked, type StepSaveResult } from './_stepContext'
import { ValuationStepper } from './_shared'
import { StepFooter } from '@/components/valuation/StepFooter'
import { FinancialsUploader } from './_FinancialsUploader'
import { FinancialsYearTable } from './_FinancialsYearTable'
import { FinancialsNotFilled } from './_FinancialsNotFilled'
import { FinancialsTieOut } from './_FinancialsTieOut'
import { FinancialsTyping, hasTypedFigures, type TypedFigures } from './_FinancialsTyping'
import { CLIENT_TEXT } from './_typedFinancialsText'

const BTN = 'rounded-md px-3 py-1.5 text-sm font-medium disabled:opacity-50'
const PRIMARY = `${BTN} bg-blue-700 text-white hover:bg-blue-800`
const SECONDARY = `${BTN} border border-gray-300 bg-white text-gray-800 hover:bg-gray-50`

export default function ValuationFinancials() {
  const { valuationId } = useParams<{ valuationId: string }>()
  const [view, setView] = useState<FinancialsView | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [aiUsed, setAiUsed] = useState(false)
  const [busy, setBusy] = useState(false)
  const [route, setRoute] = useState<'upload' | 'type' | null>(null)
  const [saved, setSaved] = useState(false)
  const inFlight = useRef(false)
  const typed = useRef<(() => TypedFigures) | null>(null)

  const reload = useCallback(async (id: string) => {
    setView(await fetchFinancials(id))
  }, [])

  useEffect(() => {
    if (!valuationId) return
    let live = true
    fetchFinancials(valuationId)
      .then(v => { if (live) setView(v) })
      .catch((err: unknown) => { if (live) setLoadError(formatApiError(err)) })
    return () => { live = false }
  }, [valuationId])

  /** One server call at a time; a second click while one is running does nothing. */
  async function run(work: (id: string) => Promise<void>) {
    if (!valuationId || inFlight.current) return
    inFlight.current = true
    setBusy(true)
    setError(null)
    try {
      await work(valuationId)
    } catch (err: unknown) {
      if (err instanceof StepBlocked) return  // the message is already on the screen
      const message = formatApiError(err)
      setError(message)
      toast.error(message)
    } finally {
      inFlight.current = false
      setBusy(false)
    }
  }

  const read = (body: ExtractBody = {}) =>
    run(async id => {
      const res = await extractFinancials(id, body)
      setAiUsed(res.ai_used === true)
      setNotice(res.message ?? null)
      await reload(id)
    })

  const upload = (files: File[]) =>
    run(async id => {
      for (const f of files) await uploadFinancialsDocument(id, f)
      setNotice(null)
      await reload(id)
    })

  /**
   * The one typed-figures save: "Save figures" (always) and the footer's
   * "Save and continue" (only when something changed). A failure throws, so
   * the footer stays on this step; a typing mistake is shown here (StepBlocked).
   */
  async function saveTyped(always = false): Promise<StepSaveResult> {
    const out = typed.current?.()
    if (!valuationId || !out || (!('invalid' in out) && !out.changed && !always)) return { changed: false, savedAt: null }
    setSaved(false)
    if ('invalid' in out) {
      setError(out.invalid)
      throw new StepBlocked()
    }
    setError(null)
    const next = await saveFinancials(valuationId, out.rows)
    setView(next)
    setSaved(true)
    return { changed: true, savedAt: next.updated_at ?? new Date().toISOString() }
  }

  if (loadError) {
    return (
      <div className="mx-auto max-w-7xl p-6">
        <div role="alert" className="rounded-lg border border-pal-error-border bg-pal-error-bg p-4 text-sm text-pal-error-text">{loadError}</div>
      </div>
    )
  }
  if (!view) {
    return <div className="mx-auto max-w-7xl p-6 text-sm text-pal-ink-muted">Loading financials…</div>
  }

  const heldYears = view.last_run?.held_period ?? []
  const heldScales = view.last_run?.held_scale ?? []
  const unmapped = view.last_run?.unmapped ?? []
  const problems = view.last_run?.errors ?? []
  const hasDocs = view.documents.length > 0
  const hasRows = view.rows.length > 0
  const typing = (route ?? (hasTypedFigures(view.provenance ?? {}) && !hasDocs ? 'type' : 'upload')) === 'type'
  const routeButton = (to: 'upload' | 'type', label: string) => (
    <button type="button" aria-pressed={typing === (to === 'type')} disabled={busy}
      className={typing === (to === 'type') ? PRIMARY : SECONDARY}
      onClick={() => { setRoute(to); setError(null); setSaved(false) }}>{label}</button>
  )

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-6">
      <h1 className="text-2xl font-bold text-pal-ink">Financials</h1>
      <ValuationStepper current="financials" engagementId={valuationId} />

      <div className="flex flex-wrap gap-2">
        {routeButton('upload', CLIENT_TEXT.ROUTE_UPLOAD)}
        {routeButton('type', CLIENT_TEXT.ROUTE_TYPE)}
      </div>
      {!typing && !hasDocs && <p className="text-base font-medium text-pal-ink">Upload your financial statements to get started.</p>}
      {!typing && <FinancialsUploader busy={busy} onFiles={f => void upload(f)} onRefused={m => setError(m)} />}
      {typing && (
        <FinancialsTyping key={view.updated_at ?? ''} rows={view.rows} provenance={view.provenance ?? {}} busy={busy}
          typed={typed} onSave={() => void run(async () => { await saveTyped(true) })} />
      )}
      {error && <div role="alert" className="rounded-lg border border-pal-error-border bg-pal-error-bg p-3 text-sm text-pal-error-text">{error}</div>}
      {saved && !error && <p role="status" className="text-sm text-pal-success-text">{CLIENT_TEXT.SAVED}</p>}

      {!typing && hasDocs && (
        <section className="space-y-2">
          <h2 className="text-sm font-semibold text-pal-ink">Files</h2>
          <ul className="text-sm text-pal-ink-body">
            {view.documents.map(d => <li key={d.doc_id}>{d.filename}</li>)}
          </ul>
          <button type="button" className={PRIMARY} disabled={busy} onClick={() => void read()}>Read figures</button>
          {aiUsed && <p className="text-xs text-pal-ink-muted">AI read this file to find some of the figures.</p>}
          {notice && <p className="text-sm text-pal-warning-text">{notice}</p>}
        </section>
      )}

      {heldYears.map(h => (
        <div key={`${h.filename}-${h.year}`} className="space-y-2 rounded-lg border border-pal-warning-border bg-pal-warning-bg p-4 text-sm">
          <p className="font-medium text-pal-ink">FY{h.year} from {h.filename} is on hold.</p>
          <p className="text-pal-ink-body">{h.reason}</p>
          <div className="flex gap-2">
            <button type="button" className={PRIMARY} disabled={busy} aria-label={`Confirm FY${h.year}`}
              onClick={() => void read({ confirm_years: [h.year] })}>Confirm</button>
            <button type="button" className={SECONDARY} disabled={busy} aria-label={`Discard FY${h.year}`}
              onClick={() => void read({ discard_years: [h.year] })}>Discard</button>
          </div>
        </div>
      ))}

      {heldScales.map(s => (
        <div key={s.doc_id} className="space-y-2 rounded-lg border border-pal-warning-border bg-pal-warning-bg p-4 text-sm">
          <p className="font-medium text-pal-ink">Are the figures in {s.filename} printed in {s.scale_name ?? 'thousands'}?</p>
          {s.evidence && <p className="text-pal-ink-body">{s.evidence}</p>}
          <p className="text-pal-ink-muted">None of this file&apos;s figures are in the table until you answer.</p>
          <div className="flex gap-2">
            <button type="button" className={PRIMARY} disabled={busy} aria-label={`Confirm scale for ${s.filename}`}
              onClick={() => void read({ confirm_scale: [s.doc_id] })}>Confirm</button>
            <button type="button" className={SECONDARY} disabled={busy} aria-label={`Decline scale for ${s.filename}`}
              onClick={() => void read({ decline_scale: [s.doc_id] })}>Decline</button>
          </div>
        </div>
      ))}

      <FinancialsNotFilled report={view.last_run} />

      {hasRows && <FinancialsYearTable rows={view.rows} provenance={view.provenance} />}

      {(view.tieout ?? []).map(y => (
        <FinancialsTieOut key={y.year} data={y}
          onDecide={b => void run(async id => { await decideTieout(id, b); await reload(id) })} />
      ))}

      {unmapped.length > 0 && (
        <section className="space-y-1">
          <h2 className="text-sm font-semibold text-pal-ink">Lines that could not be placed</h2>
          <ul className="text-sm text-pal-ink-body">
            {unmapped.map((u, i) => (
              <li key={i}>{u.source_label || '(no label)'}{typeof u.value === 'number' ? ` — ${u.value.toLocaleString('en-US')}` : ''} · {u.filename} · {u.reason}</li>
            ))}
          </ul>
        </section>
      )}
      {problems.length > 0 && (
        <ul className="text-sm text-pal-error-text">
          {problems.map((p, i) => <li key={i}>{p.filename}: {p.error}</li>)}
        </ul>
      )}

      <StepFooter step="financials" engagementId={valuationId ?? ''} save={saveTyped} />
    </div>
  )
}
